// Pieces shared by the shared-poll page (/p/:slug) and the poll page
// (/poll/:id): who is asking, the source split, the voter log, share buttons.
import { useState } from 'react';
import { avatarColors, firstName, formatCount, initials, plural, relativeTime } from '../lib/format';
import { copyText, displayUrl, pollShareUrl, sharePoll } from '../lib/share';
import { ShareIcon } from './Icons';

export function authorFirstName(poll) {
  return firstName(poll?.author_name || poll?.author_handle);
}

// Merge a vote response's tallies into a poll.
export function applyTallies(poll, data, votedItemId) {
  if (!poll) return poll;
  return {
    ...poll,
    items: Array.isArray(data?.items) ? mergeItems(poll.items, data.items) : poll.items,
    votes_count: data?.votes_count ?? poll.votes_count,
    voted_item_id: data?.voted_item_id ?? votedItemId ?? poll.voted_item_id,
  };
}

function mergeItems(current, tallies) {
  const byId = new Map((tallies || []).map((t) => [t.id, t]));
  return (current || []).map((it) => (byId.has(it.id) ? { ...it, ...byId.get(it.id) } : it));
}

export function optimisticVote(poll, item) {
  return {
    ...poll,
    items: (poll.items || []).map((it) => (it.id === item.id ? { ...it, votes_count: (it.votes_count || 0) + 1 } : it)),
    votes_count: (poll.votes_count || 0) + 1,
    voted_item_id: item.id,
  };
}

// A person's photo, else their initials on their account colour (avatarColors).
export function Avatar({ name, handle, src, size = 'h-9 w-9 text-[13px]' }) {
  if (src) return <img src={src} alt="" className={`${size} flex-none rounded-full object-cover`} />;
  const { bg, fg } = avatarColors(handle, name);
  return (
    <span
      className={`${size} flex flex-none items-center justify-center rounded-full font-bold`}
      style={{ backgroundColor: bg, color: fg }}
      aria-hidden="true"
    >
      {initials(name || handle)}
    </span>
  );
}

// The official mark of an authorless archive post: the Voto app icon.
export function ArchiveMark({ size = 'h-9 w-9' }) {
  return <img src="/voto/archive-mark.png" alt="" aria-hidden="true" className={`${size} flex-none rounded-full object-cover`} />;
}

export function AskingHeader({ poll, sub }) {
  const name = authorFirstName(poll);
  return (
    <div className="flex items-center gap-2.5">
      {name ? (
        <Avatar name={poll.author_name || poll.author_handle} handle={poll.author_handle} src={poll.author_avatar_url} />
      ) : (
        <ArchiveMark />
      )}
      <div className="min-w-0 text-[13px] text-voto-muted">
        <b className="block text-[15px] font-semibold text-voto-fg">{name ? `${name} is asking` : 'From the Voto archive'}</b>
        {sub}
      </div>
    </div>
  );
}

// "8 votes · 5 from your link · 3 from the feed" (the asker's view). Votes not
// from the link (feed, booth, profile, detail, older votes) count as the feed.
export function SourceSplit({ poll }) {
  const total = poll.votes_count || 0;
  const bySource = poll.votes_by_source || null;
  const fromLink = bySource ? Number(bySource.link || 0) : null;
  return (
    <p className="text-sm text-voto-muted" data-testid="source-split">
      <b className="text-[28px] font-bold leading-none text-voto-fg">{formatCount(total)}</b>
      {' '}
      {total === 1 ? 'vote' : 'votes'}
      {fromLink !== null && (
        <>
          {' · '}
          {formatCount(fromLink)} from your link · {formatCount(Math.max(0, total - fromLink))} from the feed
        </>
      )}
    </p>
  );
}

// The asker's voter log, built from voters_by_item ({item_id: [{name, mine, at?}]}).
export function VoterLog({ poll }) {
  const byItem = poll.voters_by_item || {};
  const labels = new Map((poll.items || []).map((it, i) => [String(it.id), it.description || `Option ${i + 1}`]));
  const rows = [];
  Object.entries(byItem).forEach(([itemId, voters]) => {
    (voters || []).forEach((v, i) => {
      rows.push({ key: `${itemId}-${i}`, name: v.mine ? 'You' : v.name || '', label: labels.get(String(itemId)) || 'an option', at: v.at || v.created_at || null });
    });
  });
  rows.sort((a, b) => (b.at ? new Date(b.at).getTime() : 0) - (a.at ? new Date(a.at).getTime() : 0));
  if (!rows.length) {
    return <p className="text-sm text-voto-muted">No votes yet. Share the link to get the first one.</p>;
  }
  return (
    <ul className="flex flex-col gap-3 text-sm" aria-label="Who voted" data-testid="voter-log">
      {rows.map((r) => (
        <li key={r.key} className="flex items-center gap-2.5">
          {r.name ? (
            <Avatar name={r.name} size="h-7 w-7 text-[11px]" />
          ) : (
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-voto-raised text-[11px] font-bold text-voto-muted" aria-hidden="true">?</span>
          )}
          <span className="min-w-0 flex-1 text-voto-fg">
            {r.name || 'Someone'} voted <b className="font-semibold">{r.label}</b>
          </span>
          {r.at && <em className="flex-none text-xs not-italic text-voto-muted">{relativeTime(r.at)}</em>}
        </li>
      ))}
    </ul>
  );
}

// Share link (coral, Web Share → copy) + Copy (ghost), with a toast.
export function ShareRow({ poll, onToast, onShared }) {
  const [busy, setBusy] = useState(false);

  async function share(preferCopy) {
    if (busy) return;
    setBusy(true);
    try {
      const { url, channel } = await sharePoll(poll, { preferCopy, where: 'poll_page' });
      onShared?.(url);
      if (channel === 'copy') onToast(`Link copied · ${displayUrl(url)}`);
    } catch (_) {
      onToast('Copy this link: ' + displayUrl(pollShareUrl(poll)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex gap-2.5">
      <button
        type="button"
        onClick={() => share(false)}
        disabled={busy}
        className="flex flex-1 items-center justify-center gap-2 rounded-[14px] bg-voto-coral px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-voto-coralDeep disabled:opacity-60"
      >
        <ShareIcon className="h-[18px] w-[18px]" />
        Share link
      </button>
      <button
        type="button"
        onClick={() => share(true)}
        disabled={busy}
        className="rounded-[14px] bg-voto-surface px-5 py-3 text-[15px] font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised disabled:opacity-60"
      >
        Copy
      </button>
    </div>
  );
}

export async function copyLink(url) {
  return copyText(url);
}

export function votesLine(poll) {
  return plural(poll.votes_count || 0, 'vote');
}
